import { message } from '@client/components/_ui/feedback/message';
import * as Sentry from '@sentry/react';
import { SSO_LINKED_TO_OLD_PROFILE_MESSAGE } from '@shared/constants/system/auth0';
import { TRIAL_EXPIRED_MESSAGE } from '@shared/utils/business/lite-trial';
import { generateHref } from '@shared/utils/core/navigation';

export type AuthErrorType =
    | 'email_not_verified'
    | 'email_required'
    | 'identity_mismatch'
    | 'sso_required'
    | 'invite_not_accepted'
    | 'no_org_assigned'
    | 'sso_linked_to_old_profile'
    | 'no_super_admin'
    | 'no_role_assigned'
    | 'support_account_not_approved'
    | 'support_session_required'
    | 'payment_required_for_login'
    | 'trial_expired'
    | 'user_not_found'
    | 'user_disabled'
    | 'user_has_not_allowed_migration_to_sso'
    | 'org_disabled'
    | 'org_mismatch'
    | 'org_not_found'
    | 'login_required'
    | 'access_denied'
    | 'temporary_auth_error'
    | 'missing_refresh_token'
    | 'invalid_grant'
    | 'missing_id_token'
    | 'invalid_state'
    | 'token_expired'
    | 'network_error'
    | 'unknown';

const errorMessages: Record<AuthErrorType, string> = {
    email_not_verified:
        'Please verify your email address before logging in. Check your inbox for the verification email.',
    email_required:
        'Your SSO identity provider account does not have an email address configured. Please contact your IT administrator to ensure your account has an email address set up.',
    identity_mismatch: 'Your identity could not be verified. Contact support if this persists.',
    sso_required: 'This account signs in with Azure AD SSO. Sign in with Azure AD instead of a password.',
    invite_not_accepted:
        'Accept your invitation using the link in your invitation email before signing in. If you did not receive one or the link has expired, ask your administrator to send it again.',
    no_org_assigned: "You currently don't have access to any organisation. Please contact your administrator.",
    sso_linked_to_old_profile: SSO_LINKED_TO_OLD_PROFILE_MESSAGE,
    no_super_admin: 'Your account requires super admin privileges. Please contact support for assistance.',
    no_role_assigned:
        "Your account hasn't been assigned any roles. Please contact your administrator to set up appropriate permissions.",
    support_account_not_approved: 'Your support account is pending approval. Please contact our team.',
    support_session_required: 'Support account requires an approved session to access this organisation.',
    payment_required_for_login: 'A payment is required to continue. Please update your subscription.',
    trial_expired: TRIAL_EXPIRED_MESSAGE,
    user_not_found: "This account doesn't have access to this organisation. Please contact your administrator.",
    user_disabled: 'Your account has been disabled. Please contact your administrator for assistance.',
    user_has_not_allowed_migration_to_sso: 'SSO migration is required. Please enable SSO in your user profile first.',
    org_disabled:
        'Your organisation has been temporarily disabled. Please contact your administrator or support team for assistance.',
    org_mismatch:
        "You don't have access to the requested organisation. You've been redirected to an organisation you have access to.",
    org_not_found:
        'The requested organisation could not be found. Please contact your administrator to confirm access.',
    login_required: 'Please log in to access this resource.',
    access_denied: "You don't have permission to access this resource. Please contact your administrator.",
    temporary_auth_error:
        'We had trouble finishing the sign-in request. Please try again. If this keeps happening, contact support.',
    missing_refresh_token:
        'Your session could not be renewed. This can happen after extended inactivity. Please log in again.',
    invalid_grant:
        'Your session has ended. This can happen if you logged in on another device or your session timed out. Please log in again.',
    missing_id_token: 'Your login information could not be verified. Please log in again.',
    invalid_state: 'The sign-in request expired or was already used. Please try again.',
    token_expired: 'Your session has timed out due to inactivity. Please log in again to continue where you left off.',
    network_error: 'Network error occurred. Please check your connection and try again.',
    unknown: 'An unknown error occurred. Please try again later.',
};

let scheduledLoginRedirect: ReturnType<typeof setTimeout> | undefined;

export class AuthError extends Error {
    constructor(
        public type: AuthErrorType,
        message?: string,
    ) {
        super(message || errorMessages[type]);
        this.name = 'AuthError';
    }
}

export function handleAuthError(
    error: unknown,
    options?: {
        showMessage?: boolean;
        redirectToLogin?: boolean;
        captureToSentry?: boolean;
        context?: string;
    },
): AuthError {
    const { showMessage = true, redirectToLogin = false, captureToSentry = true, context = 'auth' } = options || {};

    let authError: AuthError;

    // Identify error type
    if (error instanceof AuthError) {
        authError = error;
    } else if (error instanceof Error) {
        const errorType = identifyErrorType(error);
        authError = new AuthError(errorType, error.message);
    } else {
        authError = new AuthError('unknown');
    }

    // Show message
    if (showMessage) {
        message.destroy();
        // Always show the curated, user-facing copy for the error type — never the raw
        // Auth0/SDK message (e.g. "Missing Refresh Token (audience: ..., scope: ...)"),
        // which leaks internals and is meaningless to users. The raw message is still
        // carried on the thrown AuthError and reported to Sentry below for debugging.
        message.error({ content: errorMessages[authError.type] });
    }

    // Capture to Sentry
    const shouldCaptureToSentry =
        captureToSentry &&
        authError.type !== 'login_required' &&
        authError.type !== 'network_error' &&
        authError.type !== 'temporary_auth_error' &&
        authError.type !== 'missing_refresh_token' &&
        authError.type !== 'invalid_grant' &&
        authError.type !== 'invalid_state' &&
        authError.type !== 'token_expired';

    if (shouldCaptureToSentry) {
        Sentry.captureException(error, {
            tags: {
                error_type: authError.type,
                context,
            },
            extra: {
                original_error: error,
            },
        });
    }

    // Redirect to login
    const shouldRedirectToLogin =
        (redirectToLogin || isSessionError(authError.type)) &&
        authError.type !== 'network_error' &&
        authError.type !== 'temporary_auth_error';

    if (shouldRedirectToLogin) {
        clearTimeout(scheduledLoginRedirect);
        scheduledLoginRedirect = setTimeout(() => {
            scheduledLoginRedirect = undefined;
            window.location.href = generateHref({
                baseUrl: window.location.origin,
                to: '/login',
                searchParams: {
                    redirectTo: encodeURIComponent(`${window.location.pathname}${window.location.search}`),
                },
            });
        }, 1000);
    }

    return authError;
}

/**
 * Cancels the fallback full-page login redirect scheduled by handleAuthError.
 *
 * The fallback fires 1s after the auth error using the CURRENT window.location as
 * `redirectTo` — safe only while the browser is still on the protected route. Once a
 * caller (RequireAuth) performs its own SPA redirect to /login, the fallback would
 * capture the /login URL itself and nest it as `redirectTo`. Callers that own the
 * redirect must cancel the fallback instead of racing it.
 */
export function cancelScheduledLoginRedirect(): void {
    if (scheduledLoginRedirect !== undefined) {
        clearTimeout(scheduledLoginRedirect);
        scheduledLoginRedirect = undefined;
    }
}

export function identifyErrorType(error: Error): AuthErrorType {
    const message = error.message.toLowerCase();
    const errorDescription = getErrorDescription(error);
    const errorCode = getStringProperty(error, 'error')?.toLowerCase();
    const errorName = getStringProperty(error, 'name')?.toLowerCase();
    const errorType = getStringProperty(error, 'type')?.toLowerCase();
    const searchableErrorText = [message, errorDescription, errorCode, errorName, errorType].filter(Boolean).join(' ');

    if (errorCode) {
        switch (errorCode) {
            case 'missing_refresh_token':
                return 'missing_refresh_token';
            case 'invalid_grant':
                return 'invalid_grant';
            case 'missing_id_token':
                return 'missing_id_token';
            case 'invalid_state':
                return 'invalid_state';
            case 'login_required':
                return 'login_required';
        }
    }

    if (isInvalidAuthStateMessage(searchableErrorText)) {
        return 'invalid_state';
    }

    if (isReusedRefreshTokenMessage(searchableErrorText)) {
        return 'invalid_grant';
    }

    if (isInvalidRefreshTokenMessage(searchableErrorText)) {
        return 'invalid_grant';
    }

    const networkIndicators = [
        'network',
        'fetch',
        'load failed',
        'status code 0',
        'status 0',
        'net::err',
        'timeout',
        'timed out',
        'econnreset',
        'connection reset',
        'aborterror',
    ];

    if (networkIndicators.some((indicator) => searchableErrorText.includes(indicator))) {
        return 'network_error';
    }

    if (searchableErrorText.includes('expired')) {
        return 'token_expired';
    }

    if (searchableErrorText.includes('no_org_assigned')) {
        return 'no_org_assigned';
    }

    if (searchableErrorText.includes('payment_required')) {
        return 'payment_required_for_login';
    }

    if (searchableErrorText.includes('org_disabled')) {
        return 'org_disabled';
    }

    if (searchableErrorText.includes('email_not_verified')) {
        return 'email_not_verified';
    }

    if (searchableErrorText.includes('email_required')) {
        return 'email_required';
    }

    if (searchableErrorText.includes('org_not_found')) {
        return 'org_not_found';
    }

    if (searchableErrorText.includes('temporary_auth_error')) {
        return 'temporary_auth_error';
    }

    return 'unknown';
}

function isSessionError(type: AuthErrorType): boolean {
    return [
        'missing_refresh_token',
        'invalid_grant',
        'missing_id_token',
        'invalid_state',
        'login_required',
        'token_expired',
    ].includes(type);
}

function getErrorDescription(error: unknown): string | undefined {
    const description = getStringProperty(error, 'error_description');

    if (description) {
        return description.toLowerCase();
    }

    return undefined;
}

function getStringProperty(error: unknown, property: string): string | undefined {
    if (!error || typeof error !== 'object' || !(property in error)) {
        return undefined;
    }

    const value = (error as Record<string, unknown>)[property];
    return typeof value === 'string' ? value : undefined;
}

function isReusedRefreshTokenMessage(haystack: string): boolean {
    return haystack.includes('refresh token') && haystack.includes('reus');
}

function isInvalidRefreshTokenMessage(haystack: string): boolean {
    return (
        haystack.includes('unknown or invalid refresh token') ||
        (haystack.includes('refresh token') && haystack.includes('invalid')) ||
        (haystack.includes('refresh token') && haystack.includes('unknown'))
    );
}

function isInvalidAuthStateMessage(haystack: string): boolean {
    return /\binvalid[_\s]state\b/.test(haystack);
}
